import { AuthContext } from '../context/AuthContext';
import '../Style/notifications.css';
import api from '../api/userApi';
import React, { useState, useEffect, useContext, useRef } from 'react'; // Added useRef
import { useNavigate } from 'react-router-dom';
import { getFollowRequests, acceptFollowRequest, rejectFollowRequest } from '../api/userFollowApi';
import { Link } from 'react-router-dom';

function NotificationDropdown() {
  const { currentUser } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [followRequests, setFollowRequests] = useState([]);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatNotifTime = (createdAt) => {
    if (!createdAt) return '';
    const date = new Date(createdAt.endsWith('Z') ? createdAt : createdAt + 'Z');
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return isToday
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Add this filter when setting notifications
  const fetchNotifications = async () => {
    try {
      const response = await api.get('/api/notifications');
      const data = response.data.content ? response.data.content : response.data;
      const filtered = Array.isArray(data)
        ? data.filter((n) => n.type !== 'FOLLOW_REQUEST') // exclude follow requests
        : [];
      setNotifications(filtered);
    } catch (err) {
      if (err.response?.status !== 401) console.error('Error fetching notifications', err);
      setNotifications([]);
    }
  };

  const fetchFollowRequests = async () => {
    try {
      const data = await getFollowRequests();
      setFollowRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status !== 401) console.error('Error fetching follow requests', err);
    }
  };

  useEffect(() => {
    if (!currentUser?.id) return;
    fetchNotifications();
    fetchFollowRequests();
    const interval = setInterval(() => {
      fetchNotifications();
      fetchFollowRequests();
    }, 12000);
    return () => clearInterval(interval);
  }, [currentUser?.id]);

  const handleMarkAsRead = async (id) => {
    try {
      await api.put(`/api/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (err) {
      console.error('Could not mark as read', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (notifications.length === 0) return;
    try {
      await api.put('/api/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error('Could not mark all as read', err);
    }
  };

  const handleAccept = async (requesterId) => {
    try {
      await acceptFollowRequest(requesterId);
      setFollowRequests((prev) => prev.filter((r) => r.id !== requesterId));
      // Refresh notifications to clear any FOLLOW_REQUEST type
      fetchNotifications();
      fetchFollowRequests();
    } catch (err) {
      console.error('Accept failed', err);
    }
  };

  const handleReject = async (requesterId) => {
    try {
      await rejectFollowRequest(requesterId);
      setFollowRequests((prev) => prev.filter((r) => r.id !== requesterId));
      // Refresh notifications to clear any FOLLOW_REQUEST type
      fetchNotifications();
    } catch (err) {
      console.error('Reject failed', err);
    }
  };

  const handleToggle = () => {
    const opening = !isOpen;
    setIsOpen(opening);

    if (opening) {
      localStorage.setItem('notifLastSeen', Date.now().toString());
      fetchNotifications();
      fetchFollowRequests();
    }
  };

  // Add this helper above your return statement in NotificationDropdown.jsx
  const formatNotifMessage = (n) => {
    if (n.type === 'REACTION') {
      if (n.batchCount > 0) {
        return `${n.message} and ${n.batchCount} other${n.batchCount > 1 ? 's' : ''} reacted to your activity`;
      }
      return n.message; // already formatted e.g. "John Smith reacted 🔥❤️ to your activity"
    }
    return n.message;
  };

  // Red dot — only if follow request arrived AFTER last seen time
  const lastSeen = parseInt(localStorage.getItem('notifLastSeen') || '0');

  // Red dot if any follow request is newer than last seen
  const hasNewFollowRequest = followRequests.some((req) => {
    const reqTime = new Date(req.requestCreatedAt).getTime();
    return reqTime > lastSeen;
  });

  // Red dot if any notification is newer than last seen
  const hasNewNotification = notifications.some((n) => {
    const notifTime = new Date(n.createdAt).getTime();
    return notifTime > lastSeen && !n.read;
  });

  const showRedDot = hasNewFollowRequest || hasNewNotification;
  followRequests.length > 0 &&
    followRequests.some((req) => {
      const reqTime = new Date(req.requestCreatedAt).getTime();
      return reqTime > lastSeen;
    });

  const shortenName = (name, maxLength = 18) => {
    if (!name) return '';
    return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
  };

  const renderReactionMessage = (n) => {
    const message = formatNotifMessage(n);

    const splitText = ' reacted';
    const index = message.indexOf(splitText);

    if (index === -1) return message;

    const name = message.substring(0, index);
    const rest = message.substring(index);

    return (
      <>
        <span className="notif-name-short">{shortenName(name)}</span>
        <span>{rest}</span>
      </>
    );
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  const totalBadge = unreadCount + followRequests.length;

  return (
    <div className="notifications-container" ref={dropdownRef}>
      <button className="bell-btn" onClick={handleToggle}>
        🔔
        {showRedDot && <span className="unread-dot"></span>}
      </button>

      {isOpen && (
        <div className="notifications-dropdown">
          <div className="notif-header">
            <div className="notif-header-title">
              <h4>Notifications</h4>
              {totalBadge > 0 && <span className="unread-badge-count">{totalBadge}</span>}
            </div>
            {unreadCount > 0 && (
              <button className="mark-as-read-link" onClick={handleMarkAllAsRead}>
                Mark all as read
              </button>
            )}
          </div>

          <div className="notif-list">
            {/* Follow Requests Section */}
            {followRequests.length > 0 && (
              <div className="follow-requests-section">
                <div className="follow-requests-label">
                  👥 Follow Requests ({followRequests.length})
                </div>
                {followRequests.map((req) => (
                  <div key={req.id} className="follow-request-item">
                    <Link
                      to={`/profile/${req.id}`}
                      className="follow-request-name"
                      onClick={() => setIsOpen(false)}
                    >
                      {req.firstName} {req.lastName}
                    </Link>
                    <div className="follow-request-actions">
                      <button className="follow-accept-btn" onClick={() => handleAccept(req.id)}>
                        ✓
                      </button>
                      <button className="follow-reject-btn" onClick={() => handleReject(req.id)}>
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
                <div className="notif-divider" />
              </div>
            )}

            {/* Regular Notifications */}
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`notif-item ${!n.read ? 'unread' : ''}`}
                onClick={async () => {
                  if (!n.read) handleMarkAsRead(n.id);
                  if (n.targetUrl) {
                    setIsOpen(false);
                    navigate(n.targetUrl);
                  }
                }}
                style={{ cursor: n.targetUrl ? 'pointer' : 'default' }}
              >
                <div className="notif-content-wrapper">
                  <span className="notif-message">
                    {n.type === 'REACTION' ? renderReactionMessage(n) : formatNotifMessage(n)}
                  </span>
                  <span className="notif-time">{formatNotifTime(n.createdAt)}</span>
                </div>
                {n.targetUrl && <span className="notif-link-indicator">→</span>}
              </div>
            ))}

            {notifications.length === 0 && followRequests.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                No notifications yet
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationDropdown;
